import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Condition } from "@dndModels";
import { SourceBookType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CheckBox from "@components/input/CheckBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

interface Props {
  initial_filter?: any;
  on_save?: () => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

const rows_per_page = 25;

type SortOption = "Name" | "Max Level" | "Common";

export default function ConditionManager({
  initial_filter,
  on_save
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [filtered_conditions, setFilteredConditions] = useState<Condition[]>([]);
  const [selected_conditions, setSelectedConditions] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<any>(initial_filter || {});
  
  const [source_book_type, setSourceBookType] = useState<SourceBookType | "">("");
  const [source_book_id, setSourceBookId] = useState("");
  
  const [sort_by, setSortBy] = useState<SortOption>("Name");
  const [sort_ascending, setSortAscending] = useState(true);
  
  const [page, setPage] = useState(0);

  const sortConditions = useCallback((conditions_to_sort: Condition[], use_sort_by: SortOption, asc: boolean) => {
    const sorted = [...conditions_to_sort];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (use_sort_by) {
        case "Name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "Max Level":
          comparison = a.max_level - b.max_level;
          // Secondary sort by name if max levels are equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case "Common":
          // Sort common conditions first (or last depending on direction)
          comparison = (a.common ? 1 : 0) - (b.common ? 1 : 0);
          // Secondary sort by name
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
      }
      
      return asc ? comparison : -comparison;
    });
    
    return sorted;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const condition_objects = await api.getObjects({ data_type: "condition" }) as Condition[];
      setConditions(condition_objects);
      
      // Apply initial filter if provided
      let filtered: Condition[];
      if (initial_filter && Object.keys(initial_filter).length > 0) {
        filtered = api.filter("condition", condition_objects, initial_filter);
      } else {
        filtered = condition_objects;
      }
      
      // Apply sorting
      const sorted = sortConditions(filtered, sort_by, sort_ascending);
      setFilteredConditions(sorted);
    } catch (error) {
      console.error('Error loading conditions:', error);
    }
    setLoading(false);
  }, [initial_filter, sortConditions, sort_by, sort_ascending]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const handleFilterChange = useCallback((new_filters: any) => {
    setFilters(new_filters);
    const filtered = api.filter("condition", conditions, new_filters);
    const sorted = sortConditions(filtered, sort_by, sort_ascending);
    setFilteredConditions(sorted);
    setPage(0);
  }, [conditions, sort_by, sort_ascending, sortConditions]);

  const handleSortChange = useCallback((new_sort: SortOption) => {
    setSortBy(new_sort);
    const sorted = sortConditions(filtered_conditions, new_sort, sort_ascending);
    setFilteredConditions(sorted);
    setPage(0);
  }, [filtered_conditions, sort_ascending, sortConditions]);

  const handleToggleSortDirection = useCallback(() => {
    const asc = !sort_ascending;
    setSortAscending(asc);
    const sorted = sortConditions(filtered_conditions, sort_by, asc);
    setFilteredConditions(sorted);
    setPage(0);
  }, [filtered_conditions, sort_by, sort_ascending, sortConditions]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_conditions
          .map(condition => condition._id)
      );
      setSelectedConditions(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      setSelectedConditions(new Set());
    }
  };

  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_conditions
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(condition => condition._id)
      );
      setSelectedConditions(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      const page_ids = new Set(
        filtered_conditions
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(condition => condition._id)
      );
      setSelectedConditions(prev => {
        const filtered = new Set<string>();
        prev.forEach(id => {
          if (!page_ids.has(id)) {
            filtered.add(id);
          }
        });
        return filtered;
      });
    }
  };

  const handleSelectCondition = (condition_id: string) => {
    setSelectedConditions(prev => {
      const new_set = new Set(prev);
      if (new_set.has(condition_id)) {
        new_set.delete(condition_id);
      } else {
        new_set.add(condition_id);
      }
      return new_set;
    });
  };

  const handleAssignSourceBook = async () => {
    if (selected_conditions.size === 0) {
      alert('Please select at least one condition');
      return;
    }
    
    if (source_book_id === "") {
      alert('Please select a source book to assign to');
      return;
    }

    setSaving(true);
    try {
      const condition_ids = Array.from(selected_conditions);
      const selected_condition_objects = conditions.filter(c => condition_ids.includes(c._id));
      
      // Add new source books to existing ones
      const new_source_book = {
        type: source_book_type === "" ? "Source Book" : source_book_type,
        id: source_book_id,
        third_party: false
      };

      for (const condition of selected_condition_objects) {
        const existing_ids = condition.source_book_ids;
        if (!existing_ids.includes(source_book_id)) {
          // Check if it already has a variant in the source book.
          const variant = condition.variant_group.find(o => o.source_book_ids.includes(source_book_id));
          
          if (!variant) {
            condition.source_books_old.push(new_source_book);
          
            await api.updateObject("condition", condition);
          }
        }
      }
      
      // Reload conditions to reflect changes
      await load();
      setSelectedConditions(new Set());
      alert(`Successfully assigned ${condition_ids.length} condition(s) to source book(s)`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error assigning source book:', error);
      alert('Error assigning source book');
    }
    setSaving(false);
  };

  const handleNonVersionVariants = async () => {
    if (selected_conditions.size === 0) {
      alert('Please select at least one condition');
      return;
    }

    setSaving(true);
    try {
      const condition_ids = Array.from(selected_conditions);
      const selected_condition_objects = conditions.filter(c => condition_ids.includes(c._id));
      
      let cleaned = 0;
      for (const condition of selected_condition_objects) {
        if (condition.non_version_variant) {
          condition.variant_of_id = "";
          condition.variant_ids = [];
          condition.variant_group = [];
          condition.non_version_variant = false;
          await api.updateObject("condition", condition);
          cleaned++;
        }
      }
      
      // Reload conditions to reflect changes
      await load();
      setSelectedConditions(new Set());
      alert(`Successfully cleaned Non-version Variant of ${cleaned} condition(s)`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error cleaning Non-version Variant:', error);
      alert('Error cleaning Non-version Variant');
    }
    setSaving(false);
  };

  const isSelected = (condition_id: string) => selected_conditions.has(condition_id);

  const current_page_conditions = filtered_conditions.slice(
    page * rows_per_page,
    page * rows_per_page + rows_per_page
  );

  const all_page_selected = current_page_conditions.length > 0 && 
    current_page_conditions.every(condition => selected_conditions.has(condition._id));

  if (loading || !fetched) {
    return <LoadingBox />;
  }

  return (
    <Grid container spacing={1}>
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Condition Source Book Manager
      </Grid>
      {/* Filter Section */}
      <Grid
        size={{
          xs: 12
        }}>
        <FilterBox
          data_type="condition"
          the_filter={filters}
          dont_use_api
          all_objects={conditions}
          onChange={handleFilterChange}
        />
      </Grid>
      {/* Sort Section */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Sort Options
      </Grid>
      <Grid
        size={{
          xs: 8
        }}>
        <SelectStringBox
          name="Sort By"
          value={sort_by}
          options={["Name", "Max Level", "Common"]}
          onChange={handleSortChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <ButtonBox
          name={sort_ascending ? "Ascending ↑" : "Descending ↓"}
          onClick={handleToggleSortDirection}
        />
      </Grid>
      {/* Source Book Selection */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Assign to Source Book
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectSourceBookBox
          type={source_book_type}
          id={source_book_id}
          onChange={(type, id) => {
            setSourceBookType(type);
            setSourceBookId(id);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Assign ${selected_conditions.size} Selected Condition(s)`}
          disabled={
            selected_conditions.size === 0 || 
            source_book_id === "" ||
            saving
          }
          onClick={handleAssignSourceBook}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Clean Non-version variants for ${selected_conditions.size} Selected`}
          disabled={selected_conditions.size === 0 || saving}
          onClick={handleNonVersionVariants}
        />
      </Grid>
      {/* Condition List */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Conditions ({filtered_conditions.length} total, {selected_conditions.size} selected)
      </Grid>
      {/* Select All Checkbox */}
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select ALL"
          value={selected_conditions.size === filtered_conditions.length && filtered_conditions.length > 0}
          onChange={handleSelectAll}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select All on Page"
          value={all_page_selected}
          onChange={handleSelectAllOnPage}
        />
      </Grid>
      {/* Condition Rows */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row" className="grid-striped">
          {current_page_conditions.map((condition) => {
            const is_item_selected = isSelected(condition._id);
            const detail = condition.detail_format; // char_util.getStringWithReplacementsWithObject({ 
            //   clean_me: condition.detail_format, 
            //   backup: "", 
            //   obj: condition 
            // });
            
            // Build additional info string
            const info_parts: string[] = [];
            if (condition.common) {
              info_parts.push("Common");
            }
            if (condition.max_level > 0) {
              info_parts.push(`Max Level: ${condition.max_level}`);
            }
            if (condition.immunity_exists) {
              info_parts.push("Immunity Exists");
            }
            if (condition.exhaustion) {
              info_parts.push("Exhaustion");
            }
            if (condition.encumbrance) {
              info_parts.push("Encumbrance");
            }
            const info_string = info_parts.join(", ");
            
            return (
              <Grid
                key={condition._id}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    className="centered"
                    size={{
                      xs: 1
                    }}>
                    <Checkbox 
                      checked={is_item_selected}
                      onChange={() => handleSelectCondition(condition._id)}
                    />
                  </Grid>
                  <Grid
                    className={is_item_selected ? "clickable selected" : "clickable"}
                    onClick={() => handleSelectCondition(condition._id)}
                    size={{
                      xs: 11
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        className="header"
                        size={{
                          xs: 12
                        }}>
                        {condition.name}
                      </Grid>
                      { info_string.length > 0 && 
                        <Grid
                          size={{
                            xs: 12
                          }}>
                          {info_string}
                        </Grid>
                      }
                      { detail.length > 0 && 
                        <Grid
                          className="small_label gray"
                          size={{
                            xs: 12
                          }}>{ detail }</Grid> 
                      }
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      {current_page_conditions.length === 0 && (
        <Grid
          className="centered"
          size={{
            xs: 12
          }}>
          No conditions found
        </Grid>
      )}
      {/* Pagination */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Previous"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            />
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 8
            }}>
            Page {page + 1} of {Math.ceil(filtered_conditions.length / rows_per_page)}
            {' '}(Showing {page * rows_per_page + 1}-{Math.min((page + 1) * rows_per_page, filtered_conditions.length)} of {filtered_conditions.length})
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Next"
              disabled={(page + 1) * rows_per_page >= filtered_conditions.length}
              onClick={() => setPage(page + 1)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}